import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

/**
 * Context for the App Demo wizard
 */
const AppDemoContext = createContext();

/**
 * Custom hook to use the app demo context
 */
export const useAppDemo = () => {
    const context = useContext(AppDemoContext);
    if (!context) {
        throw new Error('useAppDemo must be used within an AppDemoProvider');
    }
    return context;
};

/**
 * Provider component for the App Demo context
 */
export const AppDemoProvider = ({ children, initialData = {} }) => {
    // PREVENT INFINITE LOOP: Use a ref to track if we've already initialized
    const initializedRef = useRef(false);
    
    // Load saved state from localStorage - function definition only, don't call yet
    const loadSavedStateFromStorage = () => {
        try {
            // First check if user is logged in
            const userData = localStorage.getItem('userData');
            if (!userData) {
                console.log('AppDemoProvider init: No user data found, not loading saved state');
                return {};
            }

            // Try to load app demo state from localStorage
            const savedState = localStorage.getItem('appDemoState');
            if (!savedState) {
                console.log('AppDemoProvider init: No saved app demo state found');
                return {};
            }

            // Parse the saved state
            const parsedState = JSON.parse(savedState);
            console.log('AppDemoProvider init: Loaded state from localStorage with appName:', parsedState.appName);
            console.log('AppDemoProvider init: App assets in loaded state:', parsedState.appAssets ? Object.keys(parsedState.appAssets).length : 0);
            
            // Return the parsed state to use for initialization
            return parsedState;
        } catch (error) {
            console.error('AppDemoProvider init: Error loading state from localStorage:', error);
            return {};
        }
    };

    // PREVENT INFINITE LOOP: Start with empty state
    // We'll load saved state in useEffect to prevent render loops
    const [savedState, setSavedState] = useState({});
    const mergedInitialData = { ...savedState, ...initialData };
    
    // PREVENT INFINITE LOOP: Move security check to a ref so it doesn't run on every render
    const securityCheckRef = useRef(() => {
        if (!localStorage.getItem('userData')) {
            console.log('AppDemoProvider: Security check failed - no user data found');
            localStorage.removeItem('appDemoState');
            return false;
        }
        return true;
    });
    
    // Run security check once on initial render
    const isAuthenticated = securityCheckRef.current();
    
    // User data - important for API calls
    const [user, setUser] = useState(null);
    
    // Organization data - also extract from URL if available
    const [organization, setOrganization] = useState(mergedInitialData.organization || null);
    
    // Extract organizationId from URL on component mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const urlOrgId = params.get('organizationId') || params.get('org');
        
        if (urlOrgId && (!organization || !organization.id)) {
            console.log('AppDemoContext: Found organization ID in URL:', urlOrgId);
            setOrganization(prevOrg => ({
                ...(prevOrg || {}),
                id: urlOrgId
            }));
        }
    }, [organization]);
    
    // Current step in the wizard
    const [currentStep, setCurrentStep] = useState(mergedInitialData.step || 0);
    
    // Brand analysis data
    const [brandData, setBrandData] = useState(mergedInitialData.brandData || null);
    const [selectedLogo, setSelectedLogo] = useState(mergedInitialData.selectedLogo || null);
    const [selectedBanner, setSelectedBanner] = useState(mergedInitialData.selectedBanner || null);
    
    // Additional brand-related data from comprehensive fetch
    const [domainData, setDomainData] = useState(mergedInitialData.domainData || null);
    const [aiData, setAiData] = useState(mergedInitialData.aiData || null);
    const [appIdeas, setAppIdeas] = useState(mergedInitialData.appIdeas || []);
    const [features, setFeatures] = useState(mergedInitialData.features || []);
    const [colorSchemes, setColorSchemes] = useState(mergedInitialData.colorSchemes || []);
    const [marketingTips, setMarketingTips] = useState(mergedInitialData.marketingTips || []);
    const [contentCategories, setContentCategories] = useState(mergedInitialData.contentCategories || []);
    const [brandAnalysis, setBrandAnalysis] = useState(mergedInitialData.brandAnalysis || null);
    const [appSuggestions, setAppSuggestions] = useState(mergedInitialData.appSuggestions || null);
    const [technicalRequirements, setTechnicalRequirements] = useState(mergedInitialData.technicalRequirements || []);
    const [brandDataFetched, setBrandDataFetched] = useState(mergedInitialData.brandDataFetched || false);
    const [fetchAttempts, setFetchAttempts] = useState(mergedInitialData.fetchAttempts || 0);
    const [fetchFailed, setFetchFailed] = useState(mergedInitialData.fetchFailed || false);
    
    // App customization data
    const [appName, setAppName] = useState(mergedInitialData.appName || '');
    
    // Log app name changes for debugging
    useEffect(() => {
        console.log('AppName changed to:', appName);
    }, [appName]);
    
    const [appDescription, setAppDescription] = useState(mergedInitialData.appDescription || '');
    const [appIcon, setAppIcon] = useState(mergedInitialData.appIcon || null);
    const [primaryColor, setPrimaryColor] = useState(mergedInitialData.primaryColor || '');
    const [secondaryColor, setSecondaryColor] = useState(mergedInitialData.secondaryColor || '');
    const [appFunctionalities, setAppFunctionalities] = useState(mergedInitialData.appFunctionalities || [
        { id: 'ebooks', name: 'eBooks', selected: true },
        { id: 'audiobooks', name: 'Audiobooks', selected: false },
        { id: 'courses', name: 'Online Courses', selected: false },
        { id: 'mobile', name: 'Mobile App', selected: true }
    ]);
    
    // Book content data
    const [bookTopic, setBookTopic] = useState(mergedInitialData.bookTopic || '');
    const [bookTitles, setBookTitles] = useState(mergedInitialData.bookTitles || []);
    const [bookSearchTerm, setBookSearchTerm] = useState(mergedInitialData.bookSearchTerm || '');
    const [bookQueries, setBookQueries] = useState(mergedInitialData.bookQueries || []);
    
    // Video data
    const [splashVideo, setSplashVideo] = useState(mergedInitialData.splashVideo || null);
    const [videoSearchTerm, setVideoSearchTerm] = useState(mergedInitialData.videoSearchTerm || '');
    const [videoQueries, setVideoQueries] = useState(mergedInitialData.videoQueries || []);
    
    // App Assets data
    const [appAssets, setAppAssets] = useState(mergedInitialData.appAssets || {});
    
    // Website URL for brand analysis
    const [websiteUrl, setWebsiteUrl] = useState(mergedInitialData.websiteUrl || '');
    
    // Organization name
    const [organizationName, setOrganizationName] = useState(mergedInitialData.organizationName || '');
    
    // Overall progress
    const [progress, setProgress] = useState(mergedInitialData.progress || {});
    
    // Loading states
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    
    // Whether progress is saved automatically
    const [autoSave, setAutoSave] = useState(true);
    
    // Time tracking
    const [timeSpent, setTimeSpent] = useState(mergedInitialData.timeSpent || {});
    const [startTime, setStartTime] = useState(null);
    
    // Track if we've already loaded from localStorage to prevent double loading
    const loadedFromStorage = useRef(Object.keys(savedState).length > 0);

    // Material theme data
    const [materialPalettes, setMaterialPalettes] = useState(mergedInitialData.materialPalettes || []);
    const [selectedPaletteIndex, setSelectedPaletteIndex] = useState(mergedInitialData.selectedPaletteIndex || 0);
    const [selectedPalette, setSelectedPalette] = useState(mergedInitialData.selectedPalette || null);
    const [savedCustomPalettes, setSavedCustomPalettes] = useState(mergedInitialData.savedCustomPalettes || []);

    // PREVENT INFINITE LOOP: Load saved state in a single useEffect that runs once
    useEffect(() => {
        // Skip if we've already initialized
        if (initializedRef.current) {
            return;
        }
        
        // Mark as initialized to prevent multiple loads
        initializedRef.current = true;
        
        // Load state from localStorage
        const loadedState = loadSavedStateFromStorage();
        
        // Only update state if we found something
        if (Object.keys(loadedState).length > 0) {
            setSavedState(loadedState);
            loadedFromStorage.current = true;
        }
    }, []);
    
    // Track time spent on each step
    useEffect(() => {
        if (startTime) {
            const elapsedTime = Date.now() - startTime;
            setTimeSpent(prev => ({
                ...prev,
                [currentStep]: (prev[currentStep] || 0) + elapsedTime
            }));
        }
        setStartTime(Date.now());
    }, [currentStep]);
    
    // Add a debounced save function to save state to localStorage
    const saveStateToStorage = () => {
        if (!autoSave) return;
        
        try {
            // Check if user is logged in
            const userData = localStorage.getItem('userData');
            if (!userData) {
                console.log('AppDemoProvider saveState: No user data found, not saving state');
                return;
            }
            
            // Log app assets being saved
            const assetCount = appAssets ? Object.keys(appAssets).length : 0;
            console.log(`AppDemoProvider saveState: Saving state with ${assetCount} app assets`);
            
            // Save current state to localStorage
            const stateToSave = {
                step: currentStep,
                brandData,
                appName,
                appDescription,
                appIcon,
                primaryColor,
                secondaryColor,
                appFunctionalities,
                bookTopic,
                bookTitles,
                splashVideo,
                websiteUrl,
                organizationName,
                progress,
                timeSpent,
                selectedLogo,
                selectedBanner,
                bookSearchTerm,
                videoSearchTerm,
                bookQueries,
                videoQueries,
                domainData,
                aiData,
                appIdeas,
                features, 
                colorSchemes,
                marketingTips,
                contentCategories,
                brandAnalysis,
                appSuggestions,
                technicalRequirements,
                brandDataFetched,
                fetchAttempts,
                fetchFailed,
                // Material theme data
                materialPalettes,
                selectedPaletteIndex,
                selectedPalette,
                savedCustomPalettes,
                // Include organization data
                organization,
                // App assets data
                appAssets
            };
            
            localStorage.setItem('appDemoState', JSON.stringify(stateToSave));
            console.log('AppDemoProvider saveState: State saved successfully');
        } catch (error) {
            console.error('AppDemoProvider saveState: Error saving state to localStorage:', error);
        }
    };
    
    // Load app demo state from localStorage on mount - but only if there's a logged-in user
    // AND we haven't already loaded it during initialization
    useEffect(() => {
        // Skip if we already loaded data during initialization
        if (loadedFromStorage.current) {
            console.log('AppDemoContext: Skipping localStorage load, already loaded during initialization');
            return;
        }

        try {
            // First check if user is logged in by checking userData in localStorage
            const userData = localStorage.getItem('userData');
            if (!userData) {
                console.log('No user data found in localStorage, not loading app demo state');
                localStorage.removeItem('appDemoState'); // Clean up any existing state for security
                return;
            }

            const savedState = localStorage.getItem('appDemoState');
            if (savedState) {
                const parsedState = JSON.parse(savedState);
                
                // Only load state if it's less than 24 hours old
                const lastSaved = new Date(parsedState.lastSaved);
                const now = new Date();
                const hoursDiff = (now - lastSaved) / (1000 * 60 * 60);
                
                // Check if user has changed - compare current user data with saved app state
                let shouldClearState = false;
                
                if (userData) {
                    try {
                        const parsedUserData = JSON.parse(userData);
                        // If the saved app state has an organization name that doesn't match the current user's organization,
                        // we should clear the state to prevent data from different organizations mixing
                        if (parsedState.organizationName && 
                            parsedUserData.organization_name && 
                            parsedState.organizationName !== parsedUserData.organization_name) {
                            console.log('Organization changed, clearing app demo state');
                            localStorage.removeItem('appDemoState');
                            shouldClearState = true;
                        }
                    } catch (e) {
                        console.error('Error parsing user data for organization check:', e);
                    }
                }
                
                if (shouldClearState) {
                    return; // Skip loading the state if we cleared it
                }
                
                if (hoursDiff < 24) {
                    setCurrentStep(parsedState.step || 0);
                    setBrandData(parsedState.brandData || null);
                    
                    // Sanitize the app name to remove any Penguinrandomhouse references
                    let sanitizedAppName = parsedState.appName || '';
                    if (sanitizedAppName.includes('Penguinrandomhouse')) {
                        sanitizedAppName = sanitizedAppName.replace('Penguinrandomhouse', 'Example Publisher');
                    }
                    setAppName(sanitizedAppName);
                    
                    setAppDescription(parsedState.appDescription || '');
                    setAppIcon(parsedState.appIcon || null);
                    setPrimaryColor(parsedState.primaryColor || '');
                    setSecondaryColor(parsedState.secondaryColor || '');
                    setBookTopic(parsedState.bookTopic || '');
                    setBookTitles(parsedState.bookTitles || []);
                    setSplashVideo(parsedState.splashVideo || null);
                    setWebsiteUrl(parsedState.websiteUrl || '');
                    setOrganizationName(parsedState.organizationName || '');
                    setProgress(parsedState.progress || {});
                    setTimeSpent(parsedState.timeSpent || {});
                    
                    // Load brand-related data
                    setDomainData(parsedState.domainData || null);
                    setAiData(parsedState.aiData || null);
                    setAppIdeas(parsedState.appIdeas || []);
                    setFeatures(parsedState.features || []);
                    setColorSchemes(parsedState.colorSchemes || []);
                    setMarketingTips(parsedState.marketingTips || []);
                    setContentCategories(parsedState.contentCategories || []);
                    setBrandAnalysis(parsedState.brandAnalysis || null);
                    setAppSuggestions(parsedState.appSuggestions || []);
                    setTechnicalRequirements(parsedState.technicalRequirements || []);
                    setBrandDataFetched(parsedState.brandDataFetched || false);
                    setSelectedLogo(parsedState.selectedLogo || null);
                    setSelectedBanner(parsedState.selectedBanner || null);
                    setAppFunctionalities(parsedState.appFunctionalities || []);
                    setBookSearchTerm(parsedState.bookSearchTerm || '');
                    setVideoSearchTerm(parsedState.videoSearchTerm || '');
                    setBookQueries(parsedState.bookQueries || []);
                    setVideoQueries(parsedState.videoQueries || []);
                    
                    // Load material theme data
                    setMaterialPalettes(parsedState.materialPalettes || []);
                    setSelectedPaletteIndex(parsedState.selectedPaletteIndex || 0);
                    setSelectedPalette(parsedState.selectedPalette || null);
                    setSavedCustomPalettes(parsedState.savedCustomPalettes || []);
                    
                    // Load app assets
                    setAppAssets(parsedState.appAssets || {});
                    
                    console.log('Loaded app demo state from localStorage');
                } else {
                    console.log('Saved state is too old, starting fresh');
                    localStorage.removeItem('appDemoState');
                }
            }
        } catch (error) {
            console.error('Failed to load app demo state from localStorage:', error);
            localStorage.removeItem('appDemoState'); // Clean up corrupted state
        }
    }, []);
    
    // Load user data on mount from localStorage
    useEffect(() => {
        try {
            const storedUser = localStorage.getItem('userData');
            if (storedUser) {
                const parsedUser = JSON.parse(storedUser);
                
                // Ensure we have at least the user ID available
                if (!parsedUser.id) {
                    console.warn('AppDemoContext: User data found in localStorage but missing ID');
                    // If we don't have an ID, log for debugging but still set what we have
                }
                
                console.log('AppDemoContext: Loaded user from localStorage with ID:', parsedUser.id);
                setUser(parsedUser);
                
                // After loading user, try to load state from the database
                // We'll do this asynchronously to not block the UI
                setTimeout(() => {
                    loadStateFromDatabase().then(success => {
                        if (success) {
                            console.log('Successfully loaded state from database, skipping localStorage');
                            // State has already been set from the database
                        } else {
                            console.log('Failed to load state from database, falling back to localStorage');
                            // Let the other useEffect handle loading from localStorage
                        }
                    });
                }, 100);
            } else {
                // If no user data, clear app demo state
                console.log('No user data found when loading user, clearing app demo state');
                localStorage.removeItem('appDemoState');
                // Reset all state to defaults
                resetState();
            }
        } catch (error) {
            console.error('Error loading user from localStorage:', error);
            localStorage.removeItem('appDemoState');
        }
    }, []);
    
    // Add continuous security check that runs every 10 seconds
    useEffect(() => {
        // Run initial check on component mount
        securityCheckRef.current();
        
        // Set up interval to check periodically
        const securityInterval = setInterval(() => {
            if (!securityCheckRef.current()) {
                // If security check fails, reset all state
                resetState();
            }
        }, 10000); // Check every 10 seconds
        
        // Clean up interval on unmount
        return () => clearInterval(securityInterval);
    }, []);
    
    // Save state to localStorage when state changes
    useEffect(() => {
        // Add debouncing to prevent excessive saves
        const saveTimeout = setTimeout(() => {
            saveStateToStorage();
            
            // Also save to database if the user is authenticated
            if (user?.id) {
                syncStateToDatabase();
            }
        }, 1000); // Save after 1 second of inactivity
        
        // Clean up timeout on unmount or when dependencies change
        return () => clearTimeout(saveTimeout);
    }, [
        autoSave,
        currentStep,
        brandData,
        appName,
        appDescription,
        appIcon,
        primaryColor,
        secondaryColor,
        bookTopic,
        bookTitles,
        splashVideo,
        websiteUrl,
        organizationName,
        progress,
        timeSpent,
        selectedLogo,
        selectedBanner,
        appFunctionalities,
        bookSearchTerm,
        videoSearchTerm,
        bookQueries,
        videoQueries,
        domainData,
        aiData,
        appIdeas,
        features,
        colorSchemes,
        marketingTips,
        contentCategories,
        brandAnalysis,
        appSuggestions,
        technicalRequirements,
        brandDataFetched,
        fetchAttempts,
        fetchFailed,
        materialPalettes,
        selectedPaletteIndex,
        selectedPalette,
        savedCustomPalettes,
        appAssets,
        user?.id // Add user ID to dependencies to ensure we sync on user change
    ]);
    
    /**
     * Check database health
     * @returns {Promise<boolean>} - True if database is healthy, false otherwise
     */
    const checkDatabaseHealth = async () => {
        try {
            const response = await fetch('/api/app-demo-state/health', {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();
            return data.success && data.status === 'healthy';
        } catch (error) {
            console.error('Error checking database health:', error);
            return false;
        }
    };

    // Add database status state
    const [isDatabaseAvailable, setIsDatabaseAvailable] = useState(true);
    const [lastDatabaseCheck, setLastDatabaseCheck] = useState(null);

    /**
     * Syncs the current state to the database
     */
    const syncStateToDatabase = async () => {
        if (!user?.id || !autoSave) return;
        
        try {
            console.log('Syncing app demo state to database...');
            
            // Check database health if we haven't checked recently (within 30 seconds)
            const now = Date.now();
            if (!isDatabaseAvailable || !lastDatabaseCheck || now - lastDatabaseCheck > 30000) {
                console.log('Checking database health before sync...');
                const isHealthy = await checkDatabaseHealth();
                setIsDatabaseAvailable(isHealthy);
                setLastDatabaseCheck(now);
                
                if (!isHealthy) {
                    console.warn('Database is unavailable, will use local storage only');
                }
            }
            
            // Prepare state object for database
            const stateToSync = {
                step: currentStep,
                brandData,
                appName,
                appDescription,
                appIcon,
                primaryColor,
                secondaryColor,
                appFunctionalities,
                bookTopic,
                bookTitles,
                splashVideo,
                websiteUrl,
                organizationName,
                progress,
                timeSpent,
                selectedLogo,
                selectedBanner,
                bookSearchTerm,
                videoSearchTerm,
                bookQueries,
                videoQueries,
                domainData,
                aiData,
                appIdeas,
                features, 
                colorSchemes,
                marketingTips,
                contentCategories,
                brandAnalysis,
                appSuggestions,
                technicalRequirements,
                brandDataFetched,
                fetchAttempts,
                fetchFailed,
                materialPalettes,
                selectedPaletteIndex,
                selectedPalette,
                savedCustomPalettes,
                appAssets,
                lastSynced: new Date().toISOString()
            };
            
            // Send to database
            const response = await fetch('/api/app-demo-state/sync', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    state: stateToSync,
                    organizationId: user?.organization_id || null,
                    skipDatabaseSync: !isDatabaseAvailable // Skip DB if unavailable
                })
            });
            
            const data = await response.json();
            
            // If we get a 503 Service Unavailable, mark the database as unavailable
            if (response.status === 503) {
                console.warn('Database sync failed with 503 Service Unavailable');
                setIsDatabaseAvailable(false);
                setLastDatabaseCheck(now);
                // But don't throw an error, as we've already saved to local storage
                console.log('State saved to local storage only');
                return;
            }
            
            if (!response.ok) {
                throw new Error(`Failed to sync state to database: ${response.status} ${response.statusText}`);
            }
            
            console.log('App demo state successfully synced to database');
        } catch (error) {
            console.error('Error syncing state to database:', error);
            // Mark database as unavailable if we encounter an error during sync
            setIsDatabaseAvailable(false);
            setLastDatabaseCheck(Date.now());
        }
    };
    
    /**
     * Loads app demo state from the database
     */
    const loadStateFromDatabase = async () => {
        if (!user?.id) return false;
        
        try {
            console.log('Loading app demo state from database...');
            
            const response = await fetch('/api/app-demo-state', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });
            
            // If no state found for this user, that's fine
            if (response.status === 404) {
                console.log('No app demo state found in database');
                return false;
            }
            
            if (!response.ok) {
                throw new Error(`Failed to load state from database: ${response.status} ${response.statusText}`);
            }
            
            const { state } = await response.json();
            
            if (!state) {
                console.log('No state returned from database');
                return false;
            }
            
            console.log('Successfully loaded state from database:', Object.keys(state));
            
            // Apply the state
            setCurrentStep(state.step || 0);
            setBrandData(state.brandData || null);
            setAppName(state.appName || '');
            setAppDescription(state.appDescription || '');
            setAppIcon(state.appIcon || null);
            setPrimaryColor(state.primaryColor || '');
            setSecondaryColor(state.secondaryColor || '');
            setBookTopic(state.bookTopic || '');
            setBookTitles(state.bookTitles || []);
            setSplashVideo(state.splashVideo || null);
            setWebsiteUrl(state.websiteUrl || '');
            setOrganizationName(state.organizationName || '');
            setProgress(state.progress || {});
            setTimeSpent(state.timeSpent || {});
            setDomainData(state.domainData || null);
            setAiData(state.aiData || null);
            setAppIdeas(state.appIdeas || []);
            setFeatures(state.features || []);
            setColorSchemes(state.colorSchemes || []);
            setMarketingTips(state.marketingTips || []);
            setContentCategories(state.contentCategories || []);
            setBrandAnalysis(state.brandAnalysis || null);
            setAppSuggestions(state.appSuggestions || null);
            setTechnicalRequirements(state.technicalRequirements || []);
            setBrandDataFetched(state.brandDataFetched || false);
            setSelectedLogo(state.selectedLogo || null);
            setSelectedBanner(state.selectedBanner || null);
            setAppFunctionalities(state.appFunctionalities || []);
            setBookSearchTerm(state.bookSearchTerm || '');
            setVideoSearchTerm(state.videoSearchTerm || '');
            setBookQueries(state.bookQueries || []);
            setVideoQueries(state.videoQueries || []);
            setMaterialPalettes(state.materialPalettes || []);
            setSelectedPaletteIndex(state.selectedPaletteIndex || 0);
            setSelectedPalette(state.selectedPalette || null);
            setSavedCustomPalettes(state.savedCustomPalettes || []);
            setAppAssets(state.appAssets || {});
            
            // Also save to localStorage for fast access
            localStorage.setItem('appDemoState', JSON.stringify(state));
            
            return true;
        } catch (error) {
            console.error('Error loading state from database:', error);
            return false;
        }
    };
    
    /**
     * Resets the entire app demo state
     */
    const resetState = () => {
        setCurrentStep(0);
        setBrandData(null);
        setAppName('');
        setAppDescription('');
        setAppIcon(null);
        setPrimaryColor('');
        setSecondaryColor('');
        setBookTopic('');
        setBookTitles([]);
        setSplashVideo(null);
        setWebsiteUrl('');
        setOrganizationName('');
        setProgress({});
        setTimeSpent({});
        // Reset palette-related state
        setMaterialPalettes([]);
        setSelectedPaletteIndex(0);
        setSelectedPalette(null);
        setSavedCustomPalettes([]);
        // Reset app assets
        setAppAssets({});
        localStorage.removeItem('appDemoState');
        localStorage.removeItem('savedMaterialPalettes');
    };
    
    /**
     * Goes to the next step in the wizard
     */
    const nextStep = () => {
        setCurrentStep(prev => prev + 1);
    };
    
    /**
     * Goes to the previous step in the wizard
     */
    const previousStep = () => {
        setCurrentStep(prev => Math.max(0, prev - 1));
    };
    
    /**
     * Goes to a specific step in the wizard
     */
    const goToStep = (step) => {
        setCurrentStep(step);
    };
    
    /**
     * Updates the app configuration
     */
    const updateAppConfig = (config) => {
        if (config.name !== undefined) setAppName(config.name);
        if (config.description !== undefined) setAppDescription(config.description);
        if (config.icon !== undefined) setAppIcon(config.icon);
        if (config.primaryColor !== undefined) setPrimaryColor(config.primaryColor);
        if (config.secondaryColor !== undefined) setSecondaryColor(config.secondaryColor);
    };
    
    /**
     * Sets initial data for the app demo from registration
     */
    const setInitialData = (data) => {
        if (data.websiteUrl) setWebsiteUrl(data.websiteUrl);
        if (data.organizationName) {
            setOrganizationName(data.organizationName);
            // Also pre-populate app name based on organization
            setAppName(`${data.organizationName} Books`);
        }
    };
    
    /**
     * Helper function to find the best logo from the array of logos
     */
    const findBestLogo = (logos) => {
        if (!logos || !Array.isArray(logos) || logos.length === 0) {
            console.log('No logos provided to findBestLogo');
            return null;
        }
        
        console.log('Finding best logo from:', logos);
        
        // First, try to find a proper logo (not an icon)
        const mainLogo = logos.find(logo => logo.type === 'logo' || logo.type === 'primary');
        
        if (mainLogo && mainLogo.formats && Array.isArray(mainLogo.formats) && mainLogo.formats.length > 0) {
            // Try to find SVG format first, then PNG, then any format
            const svgFormat = mainLogo.formats.find(format => format.format === 'svg');
            const pngFormat = mainLogo.formats.find(format => format.format === 'png');
            const webpFormat = mainLogo.formats.find(format => format.format === 'webp');
            const jpegFormat = mainLogo.formats.find(format => format.format === 'jpeg' || format.format === 'jpg');
            const anyFormat = mainLogo.formats[0];
            
            const selectedFormat = svgFormat || pngFormat || webpFormat || jpegFormat || anyFormat;
            
            // Create a properly structured logo object with direct src property
            return {
                type: mainLogo.type,
                src: selectedFormat.src,
                format: selectedFormat.format,
                width: selectedFormat.width,
                height: selectedFormat.height
            };
        } 
        // Check if any logo has a direct src property
        else if (mainLogo && mainLogo.src) {
            return mainLogo;
        }
        // Try to find any logo with formats
        else {
            for (const logo of logos) {
                if (logo.formats && Array.isArray(logo.formats) && logo.formats.length > 0) {
                    const format = logo.formats[0];
                    return {
                        type: logo.type,
                        src: format.src,
                        format: format.format,
                        width: format.width,
                        height: format.height
                    };
                }
            }
        }
        
        // Fallback to old logic for backward compatibility
        const svgLogo = logos.find(logo => logo.type === 'svg' || logo.format === 'svg');
        const pngLogo = logos.find(logo => logo.type === 'png' || logo.format === 'png');
        
        // Final fallback to any logo
        return svgLogo || pngLogo || logos[0];
    };
    
    // Add a polling mechanism to check for AI-generated data
    const [pollAttempts, setPollAttempts] = useState(0);
    const [isPolling, setIsPolling] = useState(false);
    const [dataLoadingState, setDataLoadingState] = useState({
        brandData: true,
        appName: true,
        appDescription: true,
        colors: true
    });
    
    useEffect(() => {
        // Skip if we already have brand data or reached max polling attempts
        if (brandDataFetched || pollAttempts > 15) {
            // If we've reached max attempts without data, mark as failed
            if (pollAttempts > 15 && !brandDataFetched) {
                setFetchFailed(true);
                setDataLoadingState({
                    brandData: false,
                    appName: false,
                    appDescription: false,
                    colors: false
                });
                console.log("Max polling attempts reached without success");
            }
            return;
        }
        
        // Skip polling if we don't have a user ID
        if (!user?.id) {
            console.log("No user ID available for polling, skipping");
            return;
        }
        
        setIsPolling(true);
        const pollTimer = setTimeout(async () => {
            try {
                console.log(`Polling for brand data (attempt ${pollAttempts + 1}/15) for user ID: ${user.id}...`);
                const response = await fetch(`/api/brand-data/by-user/${user.id}`);
                
                if (response.ok) {
                    const responseData = await response.json();
                    const data = responseData.brandData;
                    
                    // Check if we have meaningful data
                    if (data && Object.keys(data).length > 0) {
                        console.log("Polling found updated brand data:", data);
                        setBrandData(data);
                        setBrandDataFetched(true);
                        
                        // Update app details with new data
                        if (data.appName || data.name) {
                            setAppName(data.appName || data.name);
                        }
                        if (data.appDescription || data.description) {
                            setAppDescription(data.appDescription || data.description);
                        }
                        
                        // Update colors if available
                        if (data.colors && data.colors.length > 0) {
                            if (data.colors[0]) setPrimaryColor(data.colors[0].hex);
                            if (data.colors[1]) setSecondaryColor(data.colors[1].hex);
                        }
                        
                        // Update loading states
                        setDataLoadingState({
                            brandData: false,
                            appName: false,
                            appDescription: false,
                            colors: false
                        });
                        
                        // No need to poll anymore
                        setIsPolling(false);
                    } else {
                        // Increment poll attempts and continue
                        setPollAttempts(prev => prev + 1);
                    }
                } else {
                    // Increment poll attempts and continue
                    setPollAttempts(prev => prev + 1);
                    console.log(`Polling attempt ${pollAttempts + 1} failed: ${response.status} ${response.statusText}`);
                    
                    // If we've had several failures, try domain-based fetch as fallback
                    if (pollAttempts > 5 && pollAttempts % 3 === 0 && websiteUrl) {
                        console.log("Trying domain-based fetch as fallback during polling");
                        const normalizedDomain = normalizeDomain(websiteUrl);
                        if (normalizedDomain) {
                            try {
                                const domainResponse = await fetch(`/api/brand-data/by-domain/${normalizedDomain}`);
                                if (domainResponse.ok) {
                                    console.log("Domain-based fallback successful");
                                    // Process response here if needed
                                }
                            } catch (domainError) {
                                console.error("Domain-based fallback failed:", domainError);
                            }
                        }
                    }
                }
            } catch (error) {
                console.error("Error during brand data polling:", error);
                setPollAttempts(prev => prev + 1);
            }
        }, 5000); // Poll every 5 seconds
        
        return () => clearTimeout(pollTimer);
    }, [brandDataFetched, pollAttempts, websiteUrl, user?.id]);
    
    // Helper function for content categories processing
    const ensureString = (value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    };
    
    // Log video selection changes for debugging - MOVED TO AFTER STATE DECLARATIONS
    useEffect(() => {
        if (splashVideo) {
            console.log('CONTEXT: Splash video updated:', {
                id: splashVideo?.id,
                title: splashVideo?.title,
                thumbnailUrl: splashVideo?.thumbnailUrl || splashVideo?.thumbnail || splashVideo?.image
            });
            
            // FIX: Use a ref to track if normalization is needed
            // This avoids setting state in an effect that depends on that same state
            const needsNormalization = !splashVideo.thumbnailUrl && (splashVideo.thumbnail || splashVideo.image);
            
            if (needsNormalization) {
                console.log('CONTEXT: Skipping automatic normalization to avoid infinite loops');
                // Instead of setting state here, we'll log the issue
                // The component should ensure proper properties on initial creation
            }
        } else {
            console.log('CONTEXT: Splash video cleared or not set');
        }
    }, [splashVideo]);
    
    // Add a helper function to normalize domain
    const normalizeDomain = (url) => {
        if (!url) return '';
        
        let normalizedDomain = url.toLowerCase();
        
        if (normalizedDomain.startsWith('http')) {
            try {
                const urlObj = new URL(normalizedDomain);
                normalizedDomain = urlObj.hostname;
            } catch (e) {
                console.error('Error parsing URL:', e);
                normalizedDomain = normalizedDomain.replace(/^https?:\/\//, '').split('/')[0];
            }
        }
        
        return normalizedDomain.replace(/^www\./, '');
    };
    
    // The fetchBrandData function with improved error handling
    const fetchBrandData = async (websiteUrl, isRetry = false) => {
        // Check if we have user ID from context, even if it's not explicitly passed to the function
        const userId = user?.id;
        
        if (!websiteUrl && !userId) {
            console.error("No website URL or user ID provided for brand data fetch");
            return false;
        }

        try {
            setIsLoading(true);
            setLoadingMessage("Fetching brand data...");
            
            // Set loading states for brand-related data
            setDataLoadingState({
                brandData: true,
                appName: true,
                appDescription: true,
                colors: true
            });
            
            // Log what we're using to fetch brand data
            if (userId) {
                console.log(`Fetching brand data for user ID: ${userId}`);
            } else {
                console.log(`Fetching brand data for website URL: ${websiteUrl}`);
            }
            
            // First try to get user-specific brand data using the userId parameter
            let userBrandData = null;
            try {
                if (userId) {
                    console.log(`Fetching user-specific brand data for user ID: ${userId}...`);
                    const userResponse = await fetch(`/api/brand-data/by-user/${userId}`);
                    if (userResponse.ok) {
                        const responseData = await userResponse.json();
                        userBrandData = responseData.brandData;
                        console.log("Fetched user-specific brand data:", userBrandData);
                    } else {
                        console.error(`User-specific brand data fetch failed: ${userResponse.status} ${userResponse.statusText}`);
                    }
                } else {
                    console.warn("No user ID available, skipping user-specific brand data fetch");
                }
            } catch (error) {
                console.error("Error fetching user-specific brand data:", error);
            }
            
            // If we have user data, use it
            if (userBrandData && Object.keys(userBrandData).length > 0) {
                setBrandData(userBrandData);
                setBrandDataFetched(true);
                setFetchAttempts(prev => prev + 1);
                
                // Set app name and description if available
                if (userBrandData.appName || userBrandData.name) {
                    setAppName(userBrandData.appName || userBrandData.name);
                }
                
                if (userBrandData.appDescription || userBrandData.description) {
                    setAppDescription(userBrandData.appDescription || userBrandData.description);
                }
                
                // Set primary and secondary colors if available
                if (userBrandData.colors && userBrandData.colors.length > 0) {
                    if (userBrandData.colors[0]) setPrimaryColor(userBrandData.colors[0].hex);
                    if (userBrandData.colors[1]) setSecondaryColor(userBrandData.colors[1].hex);
                }
                
                // Update loading states
                setDataLoadingState({
                    brandData: false,
                    appName: false,
                    appDescription: false,
                    colors: false
                });
                
                setIsLoading(false);
                return true;
            }
            
            // Otherwise, fall back to domain-specific endpoint if we have a website URL
            if (websiteUrl) {
                console.log(`Fetching domain-based brand data for URL: ${websiteUrl}`);
                
                // Normalize the domain
                const normalizedDomain = normalizeDomain(websiteUrl);
                if (normalizedDomain) {
                    const response = await fetch(`/api/brand-data/by-domain/${normalizedDomain}`);
                    
                    if (response.ok) {
                        const responseData = await response.json();
                        const domainBrandData = responseData.brandData;
                        
                        if (domainBrandData && Object.keys(domainBrandData).length > 0) {
                            console.log("Fetched domain-based brand data:", domainBrandData);
                            setBrandData(domainBrandData);
                            setBrandDataFetched(true);
                            setFetchAttempts(prev => prev + 1);
                            
                            // Set app name and description if available
                            if (domainBrandData.name) {
                                setAppName(domainBrandData.name);
                            }
                            
                            // Set primary and secondary colors if available
                            if (domainBrandData.colors && domainBrandData.colors.length > 0) {
                                if (domainBrandData.colors[0]) setPrimaryColor(domainBrandData.colors[0].hex);
                                if (domainBrandData.colors[1]) setSecondaryColor(domainBrandData.colors[1].hex);
                            }
                            
                            // Update loading states
                            setDataLoadingState({
                                brandData: false,
                                appName: false,
                                appDescription: false,
                                colors: false
                            });
                            
                            setIsLoading(false);
                            return true;
                        }
                    } else {
                        console.error(`Domain-based brand data fetch failed: ${response.status} ${response.statusText}`);
                    }
                }
                
                // If domain-specific endpoint fails, try the old URL-based endpoint as a last resort
                console.log(`Trying legacy URL-based endpoint as fallback...`);
                const response = await fetch(`/api/brand-data/?url=${encodeURIComponent(websiteUrl)}`);
                
                if (!response.ok) {
                    console.error(`Error fetching brand data: ${response.status} ${response.statusText}`);
                    setFetchAttempts(prev => prev + 1);
                    
                    // Don't mark as failed immediately, let the polling continue
                    if (isRetry) {
                        setFetchFailed(true);
                    }
                    
                    setIsLoading(false);
                    return false;
                }
                
                const data = await response.json();
                console.log("Fetched legacy URL-based brand data:", data);
                
                setBrandData(data);
                setBrandDataFetched(true);
                setFetchAttempts(prev => prev + 1);
                
                // Set app name and description if available
                if (data.name) {
                    setAppName(data.name);
                }
                
                // Set primary and secondary colors if available
                if (data.colors && data.colors.length > 0) {
                    if (data.colors[0]) setPrimaryColor(data.colors[0].hex);
                    if (data.colors[1]) setSecondaryColor(data.colors[1].hex);
                }
                
                // Update loading states
                setDataLoadingState({
                    brandData: false,
                    appName: false,
                    appDescription: false,
                    colors: false
                });
                
                setIsLoading(false);
                return true;
            }
            
            console.error("No user ID or website URL available for brand data fetch");
            setIsLoading(false);
            return false;
        } catch (error) {
            console.error("Error in fetchBrandData:", error);
            setFetchAttempts(prev => prev + 1);
            
            // Don't mark as failed immediately, let the polling continue
            if (isRetry) {
                setFetchFailed(true);
            }
            
            setIsLoading(false);
            return false;
        }
    };
    
    const value = {
        // State
        user,
        currentStep,
        brandData,
        appName,
        appDescription,
        appIcon,
        primaryColor,
        secondaryColor,
        bookTopic,
        bookTitles,
        splashVideo,
        websiteUrl,
        organizationName,
        progress,
        isLoading,
        loadingMessage,
        autoSave,
        timeSpent,
        selectedLogo,
        selectedBanner,
        appFunctionalities,
        bookSearchTerm,
        videoSearchTerm,
        bookQueries,
        videoQueries,
        
        // New brand-related data
        domainData,
        aiData,
        appIdeas,
        features,
        colorSchemes,
        marketingTips,
        contentCategories,
        brandAnalysis,
        appSuggestions,
        technicalRequirements,
        brandDataFetched,
        fetchAttempts,
        fetchFailed,
        isPolling,
        dataLoadingState,
        
        // Material theme data
        materialPalettes,
        selectedPaletteIndex,
        selectedPalette,
        savedCustomPalettes,
        
        // App Assets
        appAssets,
        
        // Setters
        setCurrentStep,
        setBrandData,
        setAppName,
        setAppDescription,
        setAppIcon,
        setPrimaryColor,
        setSecondaryColor,
        setBookTopic,
        setBookTitles,
        setSplashVideo,
        setWebsiteUrl,
        setOrganizationName,
        setProgress,
        setIsLoading,
        setLoadingMessage,
        setAutoSave,
        setInitialData,
        setSelectedLogo,
        setSelectedBanner,
        setAppFunctionalities,
        setBookSearchTerm,
        setVideoSearchTerm,
        setBookQueries,
        setVideoQueries,
        
        // New brand-related setters
        setDomainData,
        setAiData,
        setAppIdeas,
        setFeatures,
        setColorSchemes,
        setMarketingTips,
        setContentCategories,
        setBrandAnalysis,
        setAppSuggestions,
        setTechnicalRequirements,
        setBrandDataFetched,
        
        // Material theme setters
        setMaterialPalettes,
        setSelectedPaletteIndex,
        setSelectedPalette,
        setSavedCustomPalettes,
        
        // Actions
        resetState,
        nextStep,
        previousStep,
        goToStep,
        updateAppConfig,
        fetchBrandData,
        
        // App Assets
        setAppAssets
    };
    
    return (
        <AppDemoContext.Provider value={value}>
            {children}
        </AppDemoContext.Provider>
    );
};

export default AppDemoContext; 